/**
 * Resources & case studies — client-safe part: types, hub paths, per-language lookup and route generation.
 * Pure module (no Vite features, no "@/" imports): used by the app, the build plugins and tests.
 * Parsing and the missing-fact rule live in ./pieces.ts and run only at build time (Node),
 * so the browser bundle never contains raw drafts.
 */
import { LANGS, type Lang, type SeoRoute } from "../seo/routes";

export type PieceKind = "recurso" | "caso";
export type Division = "retail" | "industrial" | "medical";

/** Hub path per kind (same in every language). */
export const KIND_BASE: Record<PieceKind, string> = { recurso: "recursos", caso: "casos" };

export interface PieceFaq { q: string; a: string }
export interface PieceRelated { path: string; label?: string }

export type Block =
  | { type: "h2" | "h3"; text: string }
  | { type: "p"; text: string }
  | { type: "quote"; text: string }
  | { type: "ul" | "ol"; items: string[] }
  | { type: "table"; head: string[] | null; rows: string[][] };

/** Front matter shared by the raw file and the published piece. */
export interface PieceMeta {
  /** Shared id across languages = file name without ".<lang>.md" */
  id: string;
  lang: Lang;
  kind: PieceKind;
  division: Division;
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  author: { name: string; role?: string; jobTitle?: string };
  published: string;
  updated: string;
  related: PieceRelated[];
  cta: string;
  image?: string;
  imageAlt?: string;
  ogImage?: string;
  mentions?: { name: string; url: string }[];
  /** The source file still has a missing-fact marker: noindex, no sitemap, no llms. */
  draft: boolean;
}

/** What the app receives: body already parsed and filtered (missing facts removed). */
export interface PublishedPiece extends PieceMeta {
  /** First paragraph, shown highlighted (direct answer). */
  lead: string | null;
  blocks: Block[];
  faq: PieceFaq[];
}

/** One piece in all its languages. */
export interface PieceGroup<P extends PieceMeta = PublishedPiece> {
  id: string;
  kind: PieceKind;
  division: Division;
  byLang: Partial<Record<Lang, P>>;
  /** Draft if ANY language file still has a missing-fact marker. */
  draft: boolean;
}

/** Language whose file is shown for `lang` (IT/DE/FR fall back to EN, then ES). */
export const contentLang = <P extends PieceMeta>(g: PieceGroup<P>, lang: Lang): Lang =>
  g.byLang[lang] ? lang : g.byLang.en ? "en" : "es";

export const pieceFor = <P extends PieceMeta>(g: PieceGroup<P>, lang: Lang): P => g.byLang[contentLang(g, lang)]!;

/** Path without language prefix, e.g. "recursos/autonomous-store-cost". */
export const piecePath = <P extends PieceMeta>(g: PieceGroup<P>, lang: Lang) => `${KIND_BASE[g.kind]}/${pieceFor(g, lang).slug}`;

/** SEO routes for every piece: per-language slugs, ES/EN (files present) indexable, drafts noindex. */
export function contentRoutes<P extends PieceMeta>(groups: PieceGroup<P>[], siteUrl: string): SeoRoute[] {
  return groups.map((g) => {
    const es = g.byLang.es ?? pieceFor(g, "es");
    const paths = Object.fromEntries(LANGS.map((l) => [l, piecePath(g, l)])) as Record<Lang, string>;
    const og = es.ogImage ?? `/og/${g.division}.jpg`;
    return {
      path: paths.es,
      paths,
      kind: g.kind === "caso" ? "case" : "article",
      parent: KIND_BASE[g.kind],
      langs: LANGS.filter((l) => g.byLang[l]),
      draft: g.draft,
      ogImage: og.startsWith("http") ? og : `${siteUrl}${og}`,
      updated: Object.values(g.byLang).map((p) => p!.updated).sort().pop(),
      priority: "0.7",
      changefreq: "monthly",
      llms: g.draft ? undefined : { title: es.h1, description: es.metaDescription },
    } satisfies SeoRoute;
  });
}

/** Inline markdown → plain text. Links become "text (absolute url)" when `siteUrl` is given. */
export const inlineToText = (s: string, siteUrl?: string) =>
  s
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text, url) =>
      siteUrl ? `${text} (${url.startsWith("/") ? siteUrl + url : url})` : text,
    )
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1$2");
